/**
 * Unified Git Data Service
 * Handles API calls to both GitHub and GitLab repositories
 * Automatically detects the provider from repository URL
 */

import config from '../config';
import { decodeBase64UTF8 } from '../utils/base64Utils';
import { getBffHeaders } from '../utils/apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { devLog } from '../utils/devLog';

class UnifiedGitDataService {
  constructor() {
    // In-flight request cache: deduplicates concurrent identical getRepositoryTree calls.
    // Key: "<provider>|<projectId>|<ref>" → pending Promise
    this._treeInflightCache = new Map();
  }

  /**
   * Detect git provider from repository URL
   * @param {string} repoUrl - Repository URL
   * @returns {string} Provider name ('github', 'gitlab', or 'azuredevops')
   */
  detectProvider(repoUrl) {
    if (!repoUrl) return 'gitlab'; // Default to gitlab for backward compatibility
    
    const url = repoUrl.toLowerCase();
    if (url.includes('github.com')) {
      return 'github';
    }
    if (url.includes('dev.azure.com') || url.includes('visualstudio.com')) {
      return 'azuredevops';
    }
    // Default to GitLab for everything else (gitlab.com, self-hosted, etc.)
    return 'gitlab';
  }

  /**
   * Get API base path for provider
   * @param {string} provider - Provider name
   * @returns {string} API base path
   */
  getProviderPath(provider) {
    return `/api/${provider}`;
  }

  /**
   * Build full URL for API endpoint
   * @param {string} provider - Provider name  
   * @param {string} endpoint - API endpoint path
   * @param {URLSearchParams} params - Query parameters
   * @returns {string} Full URL
   */
  buildUrl(provider, endpoint, params = null) {
    const basePath = this.getProviderPath(provider);
    const queryString = params ? `?${params}` : '';
    return `${config.bffUrl}${basePath}${endpoint}${queryString}`;
  }

  /**
   * Get project information
   * @param {string} projectId - Project ID
   * @param {string} repoUrl - Repository URL
   * @returns {Promise<object>} Project information
   */
  async getProject(projectId, repoUrl) {
    try {
      const provider = this.detectProvider(repoUrl);
      const basePath = this.getProviderPath(provider);

      const params = new URLSearchParams({
        project_id: projectId
      });

      const url = this.buildUrl(provider, '/project', params);

      devLog(`📦 Fetching project info from ${provider}:`, { projectId });

      const response = await getAuthorizedFetch()(url, {
        credentials: 'include',
        headers: getBffHeaders({
          'Content-Type': 'application/json'
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch project: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Failed to fetch project:', error);
      throw error;
    }
  }

  /**
   * Get repository tree structure
   * @param {string} projectId - Project ID (namespace/project format)
   * @param {string} repoUrl - Repository URL (used to detect provider)
   * @param {string} path - Path within repository (default: root)
   * @param {string} ref - Branch/tag/commit ref (default: main)
   * @returns {Promise<Array>} Repository tree items
   */
  async getRepositoryTree(projectId, repoUrl, path = '', ref = 'main') {
    const provider = this.detectProvider(repoUrl);

    // Build a deduplication key for this exact request
    const cacheKey = `${provider}|${projectId}|${ref}|${path}`;

    // If an identical request is already in flight, return the same promise
    if (this._treeInflightCache.has(cacheKey)) {
      devLog(`⚡ Deduplicating in-flight tree request: ${cacheKey}`); // eslint-disable-line no-console
      return this._treeInflightCache.get(cacheKey);
    }

    const fetchPromise = this._fetchRepositoryTree(projectId, repoUrl, provider, path, ref)
      .finally(() => {
        // Remove from cache when the request settles (success or error)
        this._treeInflightCache.delete(cacheKey);
      });

    this._treeInflightCache.set(cacheKey, fetchPromise);
    return fetchPromise;
  }

  async _fetchRepositoryTree(projectId, repoUrl, provider, path = '', ref = 'main') {
    try {
      const basePath = this.getProviderPath(provider);

      const params = new URLSearchParams({
        project_id: projectId,
        ref,
        recursive: 'true',
        per_page: 100
      });
      
      if (path) {
        params.append('path', path);
      }

      const url = this.buildUrl(provider, '/repository/tree', params);
      
      devLog(`🌳 Fetching tree from ${provider}:`, { projectId, ref, path });

      const response = await getAuthorizedFetch()(url, {
        credentials: 'include',
        headers: getBffHeaders({
          'Content-Type': 'application/json'
        })
      });

      if (!response.ok) {
        // 404 means the repository exists but is empty (no commits yet) — return empty tree
        if (response.status === 404) {
          console.warn(`⚠️ Repository tree returned 404 — repository is empty or not yet initialized`);
          return [];
        }
        // 401 is an auth failure, not a network error — throw a message that categorizeError can match
        if (response.status === 401) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || 'Unauthorized: GitLab authentication required. Please reconnect your account.');
        }
        throw new Error(`Failed to fetch repository tree: ${response.statusText}`);
      }

      // Check if GitHub tree was truncated
      const isTruncated = response.headers.get('X-GitHub-Tree-Truncated') === 'true';
      if (isTruncated) {
        console.warn('⚠️ GitHub repository tree is truncated - some files may be missing. Repository has more than 100,000 entries.');
      }

      const items = await response.json();
      
      devLog(`✅ Fetched ${items.length} items from ${provider}${isTruncated ? ' (TRUNCATED)' : ''}`);
      
      return items;
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.error('❌ BFF not running or unreachable', { error });
        throw new Error('BFF service is not running. Please ensure the backend is started.');
      }
      throw error;
    }
  }

  /**
   * Get file content from repository
   * @param {string} projectId - Project ID
   * @param {string} repoUrl - Repository URL
   * @param {string} filePath - Path to file
   * @param {string} ref - Branch/tag/commit ref
   * @returns {Promise<object>} File content object with decoded content
   */
  async getFileContent(projectId, repoUrl, filePath, ref = 'main') {
    try {
      const provider = this.detectProvider(repoUrl);
      const basePath = this.getProviderPath(provider);

      const params = new URLSearchParams({
        project_id: projectId,
        file_path: filePath,
        ref
      });

      const url = this.buildUrl(provider, '/repository/files', params);

      devLog(`📄 Fetching file from ${provider}:`, { projectId, filePath, ref });

      const response = await getAuthorizedFetch()(url, {
        credentials: 'include',
        headers: getBffHeaders({
          'Content-Type': 'application/json'
        })
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`File not found: ${filePath}`);
        }
        if (response.status === 401) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || 'Unauthorized: GitLab authentication required. Please reconnect your account.');
        }
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }

      const fileData = await response.json();

      // Decode base64 content
      if (fileData.content && fileData.encoding === 'base64') {
        fileData.decodedContent = decodeBase64UTF8(fileData.content);
      }

      return fileData;
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.error('❌ BFF not running or unreachable', { error });
        throw new Error('BFF service is not running');
      }
      throw error;
    }
  }

  /**
   * Commit changes to repository
   * @param {string} projectId - Project ID
   * @param {string} repoUrl - Repository URL
   * @param {string} branch - Target branch
   * @param {string} commitMessage - Commit message
   * @param {Array} actions - Array of file actions {action, file_path, content}
   * @returns {Promise<object>} Commit result
   */
  async commitChanges(projectId, repoUrl, branch, commitMessage, actions) {
    try {
      const provider = this.detectProvider(repoUrl);
      const basePath = this.getProviderPath(provider);

      const url = this.buildUrl(provider, '/repository/commit');

      devLog(`💾 Committing to ${provider}:`, {
        projectId,
        branch,
        actionCount: actions.length,
        message: commitMessage
      });

      const response = await getAuthorizedFetch()(url, {
        method: 'POST',
        credentials: 'include',
        headers: getBffHeaders({
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify({
          project_id: projectId,
          branch,
          commit_message: commitMessage,
          actions
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || 'Unauthorized: GitLab authentication required. Please reconnect your account.');
        }
        const errorText = await response.text();
        throw new Error(`Failed to commit changes: ${response.statusText} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Commit failed:', error);
      throw error;
    }
  }

  /**
   * List branches
   * @param {string} projectId - Project ID
   * @param {string} repoUrl - Repository URL
   * @returns {Promise<Array>} List of branches
   */
  async listBranches(projectId, repoUrl) {
    try {
      const provider = this.detectProvider(repoUrl);
      const basePath = this.getProviderPath(provider);

      const params = new URLSearchParams({
        project_id: projectId,
        per_page: 100
      });

      const url = this.buildUrl(provider, '/repository/branches', params);

      devLog(`🌿 Fetching branches from ${provider}:`, { projectId });

      const response = await getAuthorizedFetch()(url, {
        credentials: 'include',
        headers: getBffHeaders({
          'Content-Type': 'application/json'
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch branches: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Failed to fetch branches:', error);
      throw error;
    }
  }

  /**
   * Create new branch
   * @param {string} projectId - Project ID
   * @param {string} repoUrl - Repository URL
   * @param {string} branchName - New branch name
   * @param {string} ref - Source ref (branch/tag/commit)
   * @returns {Promise<object>} Created branch object
   */
  async createBranch(projectId, repoUrl, branchName, ref) {
    try {
      const provider = this.detectProvider(repoUrl);
      const basePath = this.getProviderPath(provider);

      const url = `${config.bffUrl}${basePath}/repository/branches`;

      devLog(`➕ Creating branch on ${provider}:`, { projectId, branchName, ref });

      const response = await getAuthorizedFetch()(url, {
        method: 'POST',
        credentials: 'include',
        headers: getBffHeaders({
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify({
          project_id: projectId,
          branch: branchName,
          ref
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create branch: ${response.statusText} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Failed to create branch:', error);
      throw error;
    }
  }

  /**
   * List tags
   * @param {string} projectId - Project ID
   * @param {string} repoUrl - Repository URL
   * @returns {Promise<Array>} List of tags
   */
  async listTags(projectId, repoUrl) {
    try {
      const provider = this.detectProvider(repoUrl);
      const basePath = this.getProviderPath(provider);

      const params = new URLSearchParams({
        project_id: projectId,
        per_page: 100
      });

      const url = `${config.bffUrl}${basePath}/repository/tags?${params}`;

      devLog(`🏷️ Fetching tags from ${provider}:`, { projectId });

      const response = await getAuthorizedFetch()(url, {
        credentials: 'include',
        headers: getBffHeaders({
          'Content-Type': 'application/json'
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch tags: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Failed to fetch tags:', error);
      throw error;
    }
  }

  /**
   * Create new tag
   * @param {string} projectId - Project ID
   * @param {string} repoUrl - Repository URL
   * @param {string} tagName - New tag name
   * @param {string} ref - Source ref (branch/tag/commit)
   * @param {string} message - Tag message (optional)
   * @returns {Promise<object>} Created tag object
   */
  async createTag(projectId, repoUrl, tagName, ref, message = '') {
    try {
      const provider = this.detectProvider(repoUrl);
      const basePath = this.getProviderPath(provider);

      const url = `${config.bffUrl}${basePath}/repository/tags`;

      devLog(`🏷️ Creating tag on ${provider}:`, { projectId, tagName, ref });

      const response = await getAuthorizedFetch()(url, {
        method: 'POST',
        credentials: 'include',
        headers: getBffHeaders({
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify({
          project_id: projectId,
          tag_name: tagName,
          ref,
          message
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create tag: ${response.statusText} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Failed to create tag:', error);
      throw error;
    }
  }

  /**
   * List workflow runs / CI pipelines
   * @param {string} projectId - Project ID
   * @param {string} repoUrl - Repository URL
   * @param {string} ref - Branch/tag ref (optional)
   * @param {number} perPage - Number of results per page
   * @returns {Promise<Array>} List of workflow runs/pipelines
   */
  async listWorkflowRuns(projectId, repoUrl, ref = '', perPage = 20) {
    try {
      const provider = this.detectProvider(repoUrl);
      const basePath = this.getProviderPath(provider);

      const params = new URLSearchParams({
        project_id: projectId,
        per_page: perPage.toString()
      });

      if (ref) {
        params.append('ref', ref);
      }

      const url = `${config.bffUrl}${basePath}/workflows?${params}`;

      devLog(`🔄 Fetching workflows from ${provider}:`, { projectId, ref });

      const response = await getAuthorizedFetch()(url, {
        credentials: 'include',
        headers: getBffHeaders({
          'Content-Type': 'application/json'
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch workflows: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Failed to fetch workflows:', error);
      throw error;
    }
  }

  /**
   * Get workflow/pipeline run details
   * @param {string} projectId - Project ID
   * @param {string} repoUrl - Repository URL
   * @param {string} runId - Workflow/Pipeline run ID
   * @returns {Promise<object>} Workflow run details
   */
  async getWorkflowRunDetails(projectId, repoUrl, runId) {
    try {
      const provider = this.detectProvider(repoUrl);
      const basePath = this.getProviderPath(provider);

      const params = new URLSearchParams({
        project_id: projectId
      });

      const url = `${config.bffUrl}${basePath}/workflows/${runId}?${params}`;

      devLog(`🔍 Fetching workflow details from ${provider}:`, { projectId, runId });

      const response = await getAuthorizedFetch()(url, {
        credentials: 'include',
        headers: getBffHeaders({
          'Content-Type': 'application/json'
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch workflow details: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Failed to fetch workflow details:', error);
      throw error;
    }
  }

  /**
   * Trigger a new workflow run
   * @param {string} projectId - Project ID
   * @param {string} repoUrl - Repository URL
   * @param {string} ref - Branch or tag ref
   * @param {object} inputs - Workflow inputs (optional, GitHub only)
   * @returns {Promise<object>} Created workflow run
   */
  async triggerWorkflow(projectId, repoUrl, ref, inputs = {}) {
    try {
      const provider = this.detectProvider(repoUrl);
      const basePath = this.getProviderPath(provider);

      const body = {
        project_id: projectId,
        ref
      };

      // GitHub supports workflow inputs
      if (provider === 'github' && Object.keys(inputs).length > 0) {
        body.inputs = inputs;
      }

      const url = `${config.bffUrl}${basePath}/workflows`;

      devLog(`🚀 Triggering workflow on ${provider}:`, { projectId, ref });

      const response = await getAuthorizedFetch()(url, {
        method: 'POST',
        credentials: 'include',
        headers: getBffHeaders({
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error(`Failed to trigger workflow: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Failed to trigger workflow:', error);
      throw error;
    }
  }

  /**
   * Search for projects/repositories
   * @param {string} repoUrl - Repository URL (for provider detection)
   * @param {string} search - Search query
   * @param {number} perPage - Results per page
   * @returns {Promise<Array>} List of projects
   */
  async searchProjects(repoUrl, search, perPage = 20) {
    try {
      const provider = this.detectProvider(repoUrl);
      const basePath = this.getProviderPath(provider);

      const params = new URLSearchParams({
        search,
        per_page: perPage.toString()
      });

      const url = `${config.bffUrl}${basePath}/projects?${params}`;

      devLog(`🔍 Searching projects on ${provider}:`, { search });

      const response = await getAuthorizedFetch()(url, {
        credentials: 'include',
        headers: getBffHeaders({
          'Content-Type': 'application/json'
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to search projects: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Failed to search projects:', error);
      throw error;
    }
  }

  /**
   * Get workflow/pipeline jobs
   * @param {string} projectId - Project ID
   * @param {string} repoUrl - Repository URL
   * @param {string} workflowId - Workflow/Pipeline ID
   * @returns {Promise<Array>} List of jobs
   */
  async getWorkflowJobs(projectId, repoUrl, workflowId) {
    try {
      const provider = this.detectProvider(repoUrl);
      const basePath = this.getProviderPath(provider);

      const params = new URLSearchParams({
        project_id: projectId
      });

      // Different URL structures for GitHub vs GitLab
      let url;
      if (provider === 'github') {
        url = `${config.bffUrl}${basePath}/runs/${workflowId}/jobs?${params}`;
      } else {
        // GitLab now uses /runs/:runId/jobs to match GitHub pattern
        url = `${config.bffUrl}${basePath}/runs/${workflowId}/jobs?${params}`;
      }

      devLog(`📋 Fetching workflow jobs from ${provider}:`, { projectId, workflowId });

      const response = await getAuthorizedFetch()(url, {
        credentials: 'include',
        headers: getBffHeaders({
          'Content-Type': 'application/json'
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch workflow jobs: ${response.statusText}`);
      }

      const data = await response.json();
      // GitHub returns { jobs: [...] }, GitLab returns [...]
      return Array.isArray(data) ? data : (data.jobs || []);
    } catch (error) {
      console.error('❌ Failed to fetch workflow jobs:', error);
      throw error;
    }
  }

  /**
   * Get job logs/trace
   * @param {string} projectId - Project ID
   * @param {string} repoUrl - Repository URL
   * @param {string} jobId - Job ID
   * @returns {Promise<string>} Job logs
   */
  async getJobTrace(projectId, repoUrl, jobId) {
    try {
      const provider = this.detectProvider(repoUrl);
      const basePath = this.getProviderPath(provider);

      const params = new URLSearchParams({
        project_id: projectId
      });

      // Both providers now use same URL pattern
      const url = `${config.bffUrl}${basePath}/jobs/${jobId}/logs?${params}`;

      devLog(`📝 Fetching job logs from ${provider}:`, { projectId, jobId });

      const response = await getAuthorizedFetch()(url, {
        credentials: 'include',
        headers: getBffHeaders({
          'Content-Type': 'application/json'
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch job logs: ${response.statusText}`);
      }

      return await response.text();
    } catch (error) {
      console.error('❌ Failed to fetch job logs:', error);
      throw error;
    }
  }

  /**
   * Check authentication status with Git provider
   * Uses the same endpoint as Tools page: /api/be/api/account/providers/link-status/{provider}
   * @param {string} provider - Provider name ('github', 'gitlab', or 'azuredevops')
   * @returns {Promise<object>} Authentication status and session ID if authenticated
   */
  async checkAuthentication(provider) {
    try {
      // Backend expects 'azure-devops' with hyphen, not 'azuredevops'
      const backendProvider = provider === 'azuredevops' ? 'azure-devops' : provider;
      const url = `${config.agentUrl}/api/account/providers/link-status/${backendProvider}`;
      
      devLog(`🔐 Checking ${provider} authentication with backend...`);

      const { getBeHeaders } = await import('../utils/apiHeaders');
      const response = await getAuthorizedFetch()(url, {
        method: 'GET',
        credentials: 'include',
        headers: getBeHeaders({ 'Accept': 'application/json' }),
      });

      if (response.ok) {
        const data = await response.json();
        const authenticated = data.linked === true;
        devLog(`✅ ${provider} authentication check:`, { linked: authenticated });
        return { authenticated, sessionId: null };
      }

      return { authenticated: false, sessionId: null };
    } catch (error) {
      console.error('❌ Failed to check authentication:', error);
      return { authenticated: false, sessionId: null };
    }
  }

  // ===== BACKWARD COMPATIBILITY ALIASES =====
  // These methods provide compatibility with legacy GitLabDataService naming

  /**
   * Alias for listWorkflowRuns - for backward compatibility
   * @deprecated Use listWorkflowRuns instead
   */
  async getPipelines(projectId, repoUrl, ref = '', perPage = 20) {
    return this.listWorkflowRuns(projectId, repoUrl, ref, perPage);
  }

  /**
   * Alias for getWorkflowJobs - for backward compatibility
   * @deprecated Use getWorkflowJobs instead
   */
  async getPipelineJobs(projectId, repoUrl, pipelineId) {
    return this.getWorkflowJobs(projectId, repoUrl, pipelineId);
  }

  /**
   * Alias for triggerWorkflow - for backward compatibility
   * @deprecated Use triggerWorkflow instead
   */
  async createPipeline(projectId, repoUrl, ref) {
    return this.triggerWorkflow(projectId, repoUrl, ref);
  }

  /**
   * Alias for getBranches - for backward compatibility
   * @deprecated Use listBranches instead
   */
  async getBranches(projectId, repoUrl) {
    return this.listBranches(projectId, repoUrl);
  }

  /**
   * Alias for getTags - for backward compatibility
   * @deprecated Use listTags instead
   */
  async getTags(projectId, repoUrl) {
    return this.listTags(projectId, repoUrl);
  }
}

// Export singleton instance
const unifiedGitDataService = new UnifiedGitDataService();
export default unifiedGitDataService;
